import React from 'react';
import Header from '../../components/ui/Header';
import StageCard from '../how-it-works/components/StageCard';
import VendorHeroSection from './components/VendorHeroSection';
import VendorCtaSection from './components/VendorCtaSection';

const vendorStages = [
  {
    title: 'Receive Quote Requests',
    description:
      'When a buyer creates a Stack that matches your category and service area, Stackd notifies you with a detailed project brief. Review the buyer\'s requirements, budget, timeline, and photos — then decide if it\'s the right fit for your business.',
    icon: 'Bell',
    accentColor: '#F58A54',
    bgGradient: 'linear-gradient(135deg, #F58A54 0%, #F06F6B 100%)',
    miniSteps: [
      { icon: 'Bell',       label: 'Get Notified' },
      { icon: 'FileSearch', label: 'Review Brief' },
      { icon: 'ThumbsUp',   label: 'Accept or Pass' },
    ],
    stats: [
      { value: 'Real-time', label: 'Notifications' },
      { value: 'Detailed',  label: 'Project briefs' },
      { value: 'No cost',   label: 'To review' },
    ],
    features: [
      'Instant notifications when a matching project is posted',
      'Full project brief with buyer requirements, budget, and photos',
      'Category and location-based matching so you only see relevant leads',
      'No obligation — review and decide whether to quote at no cost',
    ],
    detail:
      'Stackd\'s matching engine surfaces projects that align with your service categories and geographic coverage. You\'ll receive a structured brief with everything you need to evaluate the opportunity before committing to a quote — saving you time and ensuring every lead is worth pursuing.',
    ctaLabel: 'Go to Dashboard',
    ctaIcon: 'LayoutDashboard',
    ctaLink: '/vendor-hub-dashboard',
  },
  {
    title: 'Complete & Submit Your Quote',
    description:
      'Use Stackd\'s structured quoting tool to build a clear, competitive proposal. Break down your pricing with line items, set a validity period, add notes, and submit directly through the platform. Buyers receive your quote in a standardized format that makes it easy to evaluate.',
    icon: 'FileText',
    accentColor: '#F06F6B',
    bgGradient: 'linear-gradient(135deg, #F06F6B 0%, #E0528A 100%)',
    miniSteps: [
      { icon: 'ClipboardList', label: 'Build Quote' },
      { icon: 'DollarSign',    label: 'Set Pricing' },
      { icon: 'Send',          label: 'Submit' },
    ],
    stats: [
      { value: 'Structured', label: 'Quote format' },
      { value: 'Line items', label: 'Breakdown' },
      { value: '1 click',    label: 'Submission' },
    ],
    features: [
      'Structured quote builder with line-item pricing breakdown',
      'Schedule a visit/walkthrough as needed',
      'Submit directly through the platform — no PDFs or emails needed',
      'Buyers compare quotes and make their decision',
    ],
    detail:
      'Stackd\'s quote submission form guides you through every field the buyer needs to make a decision. Your quote is automatically formatted into the buyer\'s comparison table, putting your pricing and value proposition front and center. Clear, professional quotes win more business.',
    ctaLabel: 'View a Project',
    ctaIcon: 'Eye',
    ctaLink: '/vendor-hub-dashboard',
  },
  {
    title: 'Get Selected & Confirmed',
    description:
      'When a buyer accepts your quote, you\'ll receive an instant notification. Stackd handles the confirmation workflow — both parties are notified, the project moves to active status, and you can begin coordinating directly with the buyer through the platform\'s messaging tools.',
    icon: 'CheckCircle',
    accentColor: '#E0528A',
    bgGradient: 'linear-gradient(135deg, #E0528A 0%, #F06F6B 100%)',
    miniSteps: [
      { icon: 'Bell',         label: 'Get Notified' },
      { icon: 'Handshake',    label: 'Confirmed' },
      { icon: 'MessageCircle', label: 'Coordinate' },
    ],
    stats: [
      { value: 'Instant',  label: 'Notification' },
      { value: 'Secure',   label: 'Confirmation' },
      { value: 'In-app',   label: 'Messaging' },
    ],
    features: [
      'Instant notification when your quote is accepted',
      'Automated confirmation workflow for both vendor and buyer',
      'In-app messaging to coordinate project details and timelines',
      'Project moves to active status in your dashboard automatically',
    ],
    detail:
      'Once selected, Stackd keeps everything organized in one place. Your dashboard updates in real time, the buyer can message you directly, and all project communications are logged on the platform — giving both parties a clear record of agreements and next steps.',
    ctaLabel: 'View My Pipeline',
    ctaIcon: 'BarChart3',
    ctaLink: '/vendor-hub-dashboard',
  },
  {
    title: 'Manage Your Pipeline & Grow',
    description:
      'Track every lead, quote, and active project from your Vendor Hub dashboard. Monitor your conversion rate, average quote value, and win rate. Use analytics to understand what\'s working, improve your proposals, and grow your reputation on Stackd with verified reviews.',
    icon: 'TrendingUp',
    accentColor: '#F58A54',
    bgGradient: 'linear-gradient(135deg, #F58A54 0%, #F06F6B 100%)',
    miniSteps: [
      { icon: 'LayoutDashboard', label: 'Dashboard' },
      { icon: 'BarChart3',       label: 'Analytics' },
      { icon: 'Star',            label: 'Reviews' },
    ],
    stats: [
      { value: 'Live KPIs',  label: 'Real-time data' },
      { value: 'Pipeline',   label: 'Full visibility' },
      { value: 'Badges',     label: 'Build trust' },
    ],
    features: [
      'Real-time KPIs: active leads, conversion rate, avg quote value, win rate',
      'Pipeline view organized by quote status — pending, submitted, won, lost',
      'Analytics dashboard to track response rates and performance trends',
      'Earn Stackd badges (Top Rated, On-Time Pro, etc.) to build buyer trust',
    ],
    detail:
      'Your Vendor Hub is your command center. Every project you\'re invited to, every quote you submit, and every win is tracked and visualized so you can make smarter business decisions. Stackd\'s badge system rewards consistent, high-quality vendors with increased visibility and buyer confidence.',
    ctaLabel: 'Open My Dashboard',
    ctaIcon: 'LayoutDashboard',
    ctaLink: '/vendor-hub-dashboard',
  },
];

const VendorHowItWorks = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <VendorHeroSection />

        {/* Stages */}
        <section className="py-20 bg-background">
          <div className="max-w-6xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-lg font-semibold mb-6" style={{ color: '#F06F6B' }}>
                <em>Vendors on Stackd close more deals with less effort</em>
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Four steps to winning business
              </h2>
              <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                Stackd connects you with qualified buyers and gives you the tools to quote, win, and deliver — all in one platform.
              </p>
            </div>

            <div className="space-y-24">
              {vendorStages?.map((stage, index) => (
                <StageCard
                  key={index}
                  stage={stage}
                  index={index}
                  isReversed={index % 2 !== 0}
                />
              ))}
            </div>
          </div>
        </section>

        <VendorCtaSection />
      </main>
    </div>
  );
};

export default VendorHowItWorks;
